import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrency } from "@/lib/utils";
import { GitCompareArrows, TrendingUp, Radar, BarChart3, Table2 } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar as ReRadar,
  BarChart, Bar, Cell,
} from "recharts";

const TEAM_COLORS = [
  "#0ea5e9", "#f43f5e", "#10b981", "#f59e0b", "#8b5cf6",
  "#ec4899", "#06b6d4", "#84cc16", "#f97316", "#6366f1",
];

const METRIC_OPTIONS = [
  { value: "cumulative_profit", label: "compare.cumulativeProfit", format: "currency" },
  { value: "stock_price", label: "compare.stockPrice", format: "currency" },
  { value: "revenue", label: "compare.revenue", format: "currency" },
  { value: "net_income", label: "compare.netIncome", format: "currency" },
  { value: "load_factor", label: "compare.loadFactor", format: "percent" },
];

const RANKING_OPTIONS = [
  { value: "cumulative_profit", label: "compare.cumulativeProfit" },
  { value: "stock_price", label: "compare.stockPrice" },
  { value: "cash", label: "compare.cash" },
  { value: "market_share", label: "compare.marketShare" },
  { value: "quality_rating", label: "compare.quality" },
  { value: "aircraft_count", label: "compare.fleetSize" },
];

export default function ComparativeAnalytics({ game, team }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendMetric, setTrendMetric] = useState("cumulative_profit");
  const [rankMetric, setRankMetric] = useState("cumulative_profit");

  useEffect(() => {
    if (game?.id) {
      axios.get(`${API}/games/${game.id}/comparison-history`)
        .then(res => setData(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [game?.id]);

  const teams = data?.teams || [];
  const myTeamId = team?.id;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-slate-200 rounded w-56 animate-pulse" />
        <div className="h-80 bg-slate-200 rounded animate-pulse" />
      </div>
    );
  }

  if (!teams.length || !teams[0]?.history?.length) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <GitCompareArrows className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">{t("compare.noData")}</p>
          <p className="text-sm text-slate-400 mt-1">{t("compare.noDataDesc")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="comparative-analytics">
      <div>
        <h2 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-2">
          <GitCompareArrows className="w-6 h-6 text-sky-600" />
          {t("compare.title")}
        </h2>
        <p className="text-slate-500 text-sm">{t("compare.subtitle")}</p>
      </div>

      {/* Trend Chart */}
      <TrendSection
        teams={teams}
        myTeamId={myTeamId}
        metric={trendMetric}
        setMetric={setTrendMetric}
        t={t}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Radar */}
        <RadarSection teams={teams} myTeamId={myTeamId} t={t} />
        {/* Ranking */}
        <RankingSection
          teams={teams}
          myTeamId={myTeamId}
          metric={rankMetric}
          setMetric={setRankMetric}
          t={t}
        />
      </div>

      {/* Table */}
      <StandingsTable teams={teams} myTeamId={myTeamId} t={t} />
    </div>
  );
}

/* ===================== TREND LINES ===================== */
function TrendSection({ teams, myTeamId, metric, setMetric, t }) {
  const metaOption = METRIC_OPTIONS.find(m => m.value === metric) || METRIC_OPTIONS[0];
  const isCurrency = metaOption.format === "currency";

  const chartData = useMemo(() => {
    const quarterMap = {};
    teams.forEach((tm, idx) => {
      tm.history.forEach(h => {
        if (!quarterMap[h.quarter]) quarterMap[h.quarter] = { quarter: `Q${h.quarter}` };
        quarterMap[h.quarter][tm.airline_code] = h[metric] || 0;
      });
    });
    return Object.values(quarterMap).sort((a, b) => parseInt(a.quarter.slice(1)) - parseInt(b.quarter.slice(1)));
  }, [teams, metric]);

  const formatVal = (v) => isCurrency ? `$${(v / 1000).toFixed(0)}k` : `${v.toFixed(1)}%`;

  return (
    <Card data-testid="trend-chart-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-sky-500" />
            {t("compare.trendOverTime")}
          </CardTitle>
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="w-48 h-8 text-xs" data-testid="trend-metric-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {METRIC_OPTIONS.map(m => (
                <SelectItem key={m.value} value={m.value}>{t(m.label)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-72" data-testid="trend-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="quarter" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={formatVal} />
              <Tooltip
                formatter={(v, name) => [isCurrency ? formatCurrency(v) : `${v.toFixed(1)}%`, name]}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
              />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              {teams.map((tm, i) => (
                <Line
                  key={tm.team_id}
                  dataKey={tm.airline_code}
                  name={tm.team_name}
                  stroke={TEAM_COLORS[i % TEAM_COLORS.length]}
                  strokeWidth={tm.team_id === myTeamId ? 3 : 1.5}
                  strokeOpacity={tm.team_id === myTeamId ? 1 : 0.5}
                  dot={tm.team_id === myTeamId}
                  strokeDasharray={tm.is_ai ? "5 3" : undefined}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[10px] text-slate-400 text-center mt-1">{t("compare.aiDashed")}</p>
      </CardContent>
    </Card>
  );
}

/* ===================== RADAR ===================== */
function RadarSection({ teams, myTeamId, t }) {
  const radarFields = [
    { key: "quality_rating", label: t("compare.quality") },
    { key: "reliability", label: t("compare.reliability") },
    { key: "brand_awareness", label: t("compare.brand") },
    { key: "customer_satisfaction", label: t("compare.custSat") },
    { key: "employee_satisfaction", label: t("compare.empSat") },
  ];

  const myTeam = teams.find(tm => tm.team_id === myTeamId);
  const avgValues = {};
  radarFields.forEach(f => {
    avgValues[f.key] = teams.reduce((sum, tm) => sum + (tm.current?.[f.key] || 0), 0) / teams.length;
  });

  const leader = {};
  radarFields.forEach(f => {
    leader[f.key] = Math.max(...teams.map(tm => tm.current?.[f.key] || 0));
  });

  const radarData = radarFields.map(f => ({
    metric: f.label,
    you: myTeam?.current?.[f.key] || 0,
    avg: Math.round(avgValues[f.key] * 10) / 10,
    leader: leader[f.key],
  }));

  return (
    <Card data-testid="radar-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Radar className="w-4 h-4 text-purple-500" />
          {t("compare.radarTitle")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64" data-testid="radar-chart">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData} outerRadius="75%">
              <PolarGrid stroke="#e2e8f0" />
              <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10 }} />
              <PolarRadiusAxis tick={{ fontSize: 9 }} domain={[0, 100]} />
              <ReRadar name={t("compare.you")} dataKey="you" stroke="#0ea5e9" fill="#0ea5e9" fillOpacity={0.25} strokeWidth={2} />
              <ReRadar name={t("compare.average")} dataKey="avg" stroke="#94a3b8" fill="transparent" strokeWidth={1.5} strokeDasharray="4 3" />
              <ReRadar name={t("compare.leader")} dataKey="leader" stroke="#f59e0b" fill="transparent" strokeWidth={1.5} strokeDasharray="2 2" />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

/* ===================== RANKING BARS ===================== */
function RankingSection({ teams, myTeamId, metric, setMetric, t }) {
  const meta = RANKING_OPTIONS.find(m => m.value === metric) || RANKING_OPTIONS[0];
  const isCurrency = ["cumulative_profit", "stock_price", "cash"].includes(metric);

  const sorted = useMemo(() => {
    return [...teams]
      .map(tm => ({
        name: tm.airline_code,
        fullName: tm.team_name,
        value: tm.current?.[metric] ?? 0,
        isMe: tm.team_id === myTeamId,
        isAi: tm.is_ai,
      }))
      .sort((a, b) => b.value - a.value);
  }, [teams, metric, myTeamId]);

  return (
    <Card data-testid="ranking-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-emerald-500" />
            {t("compare.rankingTitle")}
          </CardTitle>
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="w-44 h-8 text-xs" data-testid="ranking-metric-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANKING_OPTIONS.map(m => (
                <SelectItem key={m.value} value={m.value}>{t(m.label)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-64" data-testid="ranking-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={sorted} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fontSize: 10 }}
                stroke="#94a3b8"
                tickFormatter={v => isCurrency ? `$${(v / 1000).toFixed(0)}k` : v.toFixed(0)}
              />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" width={50} />
              <Tooltip
                formatter={v => isCurrency ? formatCurrency(v) : v.toFixed(1)}
                contentStyle={{ fontSize: 11, borderRadius: 8 }}
                labelFormatter={l => sorted.find(s => s.name === l)?.fullName || l}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} name={t(meta.label)}>
                {sorted.map((entry, i) => (
                  <Cell
                    key={entry.name}
                    fill={entry.isMe ? "#0ea5e9" : entry.isAi ? "#cbd5e1" : "#94a3b8"}
                    fillOpacity={entry.isMe ? 1 : 0.7}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center gap-4 justify-center mt-2 text-[10px] text-slate-400">
          <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-sky-500 inline-block" /> {t("compare.you")}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-slate-400 inline-block" /> {t("compare.humanTeams")}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-slate-200 inline-block" /> {t("compare.aiTeams")}</span>
        </div>
      </CardContent>
    </Card>
  );
}

/* ===================== STANDINGS TABLE ===================== */
function StandingsTable({ teams, myTeamId, t }) {
  const sorted = useMemo(() => {
    return [...teams].sort((a, b) => (b.current?.cumulative_profit ?? 0) - (a.current?.cumulative_profit ?? 0));
  }, [teams]);

  return (
    <Card data-testid="standings-table-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Table2 className="w-4 h-4 text-amber-500" />
          {t("compare.standingsTitle")}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]" data-testid="standings-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 w-8">#</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">{t("compare.airline")}</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">{t("compare.cumulativeProfit")}</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">{t("compare.stockPrice")}</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">{t("compare.cash")}</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">{t("compare.quality")}</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">{t("compare.marketShare")}</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500">{t("compare.fleetSize")}</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500">{t("compare.routes")}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((tm, i) => {
                const c = tm.current || {};
                const isMe = tm.team_id === myTeamId;
                return (
                  <tr key={tm.team_id} className={`border-b border-slate-50 ${isMe ? "bg-sky-50 font-semibold" : "hover:bg-slate-50"}`} data-testid={`standing-row-${tm.team_id}`}>
                    <td className="py-2 px-3 text-xs font-mono text-slate-400">{i + 1}</td>
                    <td className="py-2 px-3">
                      <span className="font-medium">{tm.team_name}</span>
                      <span className="ml-1.5 text-xs text-slate-400 font-mono">{tm.airline_code}</span>
                      {tm.is_ai && <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-500">AI</span>}
                      {isMe && <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] bg-sky-100 text-sky-700">{t("compare.you")}</span>}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono text-xs ${c.cumulative_profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                      {formatCurrency(c.cumulative_profit || 0)}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-xs">${(c.stock_price || 0).toFixed(2)}</td>
                    <td className="py-2 px-3 text-right font-mono text-xs">{formatCurrency(c.cash || 0)}</td>
                    <td className="py-2 px-3 text-right font-mono text-xs">{(c.quality_rating || 0).toFixed(1)}</td>
                    <td className="py-2 px-3 text-right font-mono text-xs">{(c.market_share || 0).toFixed(1)}%</td>
                    <td className="py-2 px-3 text-center font-mono text-xs">{c.aircraft_count || 0}</td>
                    <td className="py-2 px-3 text-center font-mono text-xs">{c.active_routes || 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
